/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-indication.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent, DialogActions } from '@mui/material';
import WarningForDialog from '@components/admin/warning-for-dialog/warning-for-dialog';
import { useTranslation, useBoolean } from 'hooks';
import { OrganisationIndicationFields, OrganisationProgramFields } from 'queries/generated/fragments.generated';
import { SAVE_ORGANISATION_INDICATION, SAVE_ORGANISATION_PROGRAM } from 'queries/org-admin';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import { checkIfNameExist, validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

interface ICustomIndication extends OrganisationIndicationFields {
    amountStudies?: number;
    amountPrograms?: number;
}

const EditIndication = ({
    open,
    indication,
    indications,
    onClose,
    currentProgram,
}: {
    open: boolean;
    indication?: ICustomIndication;
    indications: ICustomIndication[];
    onClose: Function;
    currentProgram?: OrganisationProgramFields;
}) => {
    const { t } = useTranslation();
    const [saveIndication] = useMutation(SAVE_ORGANISATION_INDICATION);
    const [saveProgram] = useMutation(SAVE_ORGANISATION_PROGRAM);

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState(null);
    const saving = useBoolean(false);
    const disabledSave = useBoolean(true);

    useEffect(() => {
        if (open) setName(indication?.name ?? name);
    }, [indication, open]);

    const close = (action?: string) => {
        if (action !== 'error') {
            setNameError(null);
            setName('');
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const getRefetchQueries = () => {
        if (!currentProgram)
            return [
                {
                    query: GET_ORGANISATION_PROGRAM_PRODUCT,
                },
            ];

        return [];
    };

    const submit = () => {
        const error = validateFields(name, 'name', 50, true);

        const submitData = {
            id: indication?.id ?? null,
            name: name.trim(),
        };

        if (error.name) {
            setNameError(error.name);
            return;
        }

        if (checkIfNameExist(submitData, indications, 'name')) {
            setNameError(t('admin:indicationAlreadyExist'));
            return;
        }

        saving.setTrue();
        saveIndication({
            variables: {
                indication: {
                    ...submitData,
                },
            },
            refetchQueries: getRefetchQueries(),
        })
            .then((res: any) => {
                if (currentProgram) {
                    const ids = currentProgram.indicationIds.concat(res.data.saveOrganisationIndication.id);
                    saveProgram({
                        variables: {
                            program: {
                                id: currentProgram?.id ?? null,
                                name: currentProgram?.name,
                                productIds: currentProgram?.productIds ?? [],
                                indicationIds: ids,
                            },
                        },
                        refetchQueries: [
                            {
                                query: GET_ORGANISATION_PROGRAM_PRODUCT,
                            },
                        ],
                    })
                        .then(() => {
                            saving.setFalse();
                            close('add');
                        })
                        .catch(() => {
                            saving.setFalse();
                            close('error');
                        });
                } else {
                    saving.setFalse();
                    close(indication ? 'edit' : 'add');
                }
            })
            .catch(() => {
                saving.setFalse();
                close('error');
            });
    };

    const getButtonLabel = () => {
        if (currentProgram && !indication) return 'common:createAssign';
        return indication ? 'common:update' : 'common:add';
    };

    const showWarningMessage = indication?.amountStudies || indication?.amountPrograms;

    return (
        <CustomDialog onClose={close} fullWidth maxWidth="md" open={open} className="custom-width-popup">
            <DialogTitle id="edit-indication-title">
                {indication
                    ? `${t('admin:editIndication')}: ${indication?.recordId || indication?.id}`
                    : t('admin:addIndication')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 15 }}>
                {showWarningMessage ? (
                    <div className="wrap-warning-message-absolute">
                        <WarningForDialog
                            firstNumber={indication?.amountStudies}
                            secondNumber={indication?.amountPrograms}
                            type="product"
                        />
                    </div>
                ) : null}
                <TextField
                    id="edit-indication-name"
                    autoFocus
                    label={`${t('admin:indication')}*`}
                    notched
                    error={!!nameError}
                    fullWidth
                    value={name}
                    onChange={(event: any) => {
                        disabledSave.setFalse();
                        setName(event.target.value);
                        const error = validateFields(event.target.value, 'name', 50, false);
                        setNameError(error?.name ?? null);
                    }}
                    size="small"
                    placeholder={t('admin:enterIndication')}
                    helperText={
                        <CharactersRemaining
                            id="edit-indication-name-characters"
                            length={name.length}
                            max={50}
                            error={nameError}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    className="mb"
                />
            </DialogContent>
            <DialogActions
                className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${
                    !indication ? 'flex-end' : ''
                }`}
            >
                <ModalUpdatedInfo item={indication} />
                <SaveButton
                    className="edit-cv-update-btn small"
                    id="edit-indication-save"
                    text={getButtonLabel()}
                    disabled={nameError || disabledSave.value || saving.value}
                    onClick={submit}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditIndication;
